import React, { useId, useMemo } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { chartColors, useThemeVersion } from "../theme";
import { fmtTime, num } from "../lib/format";
import { Empty, Skeleton, cx } from "./ui";

// Tooltip body shared by every Recharts chart (text stays in ink colors)
export function ChartTip({ active, payload, label, title, rows }) {
  if (!active || !payload?.length) return null;
  const list = rows ? rows(payload) : payload.map((p) => ({ name: p.name, value: p.value, color: p.color }));
  return (
    <div className="viz-tip">
      <div className="viz-tip-title">{title ? title(label, payload) : label}</div>
      {list.map((r) => (
        <div className="viz-tip-row" key={r.name}>
          <span>
            {r.color && <i style={{ background: r.color }} />}
            {r.name}
          </span>
          <b>{r.value}</b>
        </div>
      ))}
    </div>
  );
}

/**
 * Activity over the last ten minutes, one series at a time (detections or
 * events analysed have very different scales, so they never share an axis).
 */
export function ActivityChart({ timeline, metric = "threats", height = 240 }) {
  useThemeVersion();
  const c = chartColors();
  const gid = useId().replace(/:/g, "");
  const color = metric === "threats" ? c.brand : c.signal;
  const name = metric === "threats" ? "Detections" : "Events analysed";
  const data = useMemo(
    () => (timeline || []).map((b) => ({ t: fmtTime(b.timestamp), v: b[metric] || 0 })),
    [timeline, metric],
  );

  if (!timeline) return <Skeleton h={height} r={10} />;
  if (data.length < 2)
    return (
      <div style={{ height, display: "grid", placeItems: "center" }}>
        <Empty icon="activity" compact title="Collecting activity">
          The first 10-second buckets arrive shortly after the backend starts.
        </Empty>
      </div>
    );

  return (
    <div style={{ height }} role="img" aria-label={`${name} per 10 seconds over the last ${Math.round(data.length / 6)} minutes`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
          <defs>
            <linearGradient id={`ag${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.26} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={c.grid} strokeDasharray="0" vertical={false} />
          <XAxis
            dataKey="t"
            tick={{ fill: c.axis, fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: c.grid }}
            interval="preserveStartEnd"
            minTickGap={48}
          />
          <YAxis tick={{ fill: c.axis, fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} width={48} />
          <Tooltip
            cursor={{ stroke: c.axis, strokeWidth: 1, strokeDasharray: "3 3" }}
            content={<ChartTip title={(l) => `${l} GST · 10 s bucket`} rows={(p) => [{ name, value: num(p[0].value), color }]} />}
          />
          <Area
            type="monotone"
            dataKey="v"
            name={name}
            stroke={color}
            strokeWidth={2}
            fill={`url(#ag${gid})`}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: c.panel, fill: color }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Ranked horizontal bars with text labels (identity never relies on color alone). */
export function BarList({ items, total, onSelect, empty = "No data yet", max = 8, valueLabel, share = true }) {
  if (!items) return <SkeletonList />;
  if (!items.length) return <Empty compact icon="barChart">{empty}</Empty>;
  const top = Math.max(...items.map((i) => i.value), 1);
  const sum = total ?? items.reduce((s, i) => s + i.value, 0);
  return (
    <div className="barlist">
      {items.slice(0, max).map((it) => {
        const Tag = onSelect ? "button" : "div";
        return (
          <Tag
            key={it.key}
            type={onSelect ? "button" : undefined}
            className="barlist-row"
            onClick={onSelect ? () => onSelect(it) : undefined}
            title={onSelect ? `Show ${it.label}` : undefined}
          >
            <div className="barlist-top">
              <span className="barlist-label">
                {it.color && <i style={{ background: it.color }} />}
                <span className="truncate">{it.label}</span>
              </span>
              <span className="barlist-value">
                <b>{valueLabel ? valueLabel(it.value) : num(it.value)}</b>
                {share && sum > 0 && `${Math.round((it.value / sum) * 100)}%`}
              </span>
            </div>
            <div className="meter" style={{ height: 6 }}>
              <i style={{ width: `${Math.max(2, (it.value / top) * 100)}%`, background: it.color || "var(--brand)" }} />
            </div>
          </Tag>
        );
      })}
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="barlist">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="barlist-row">
          <Skeleton h={12} w={`${70 - i * 8}%`} />
          <Skeleton h={6} w={`${90 - i * 14}%`} />
        </div>
      ))}
    </div>
  );
}

/** Circular score (0–100). */
export function ScoreRing({ value, color = "var(--brand)", size = 128, label = "of 100", stroke = 10 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="score-ring" style={{ width: size, height: size }} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={value ?? undefined} aria-label={`Score ${value ?? "unavailable"} ${label}`}>
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} style={{ fill: "none", stroke: "var(--panel-3)" }} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          style={{ fill: "none", stroke: color, transition: "stroke-dashoffset 800ms cubic-bezier(.2,.8,.2,1)" }}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - v / 100)}
        />
      </svg>
      <div className="score-ring-value">
        <div>
          <strong>{value == null ? "—" : Math.round(value)}</strong>
          <span>{label}</span>
        </div>
      </div>
    </div>
  );
}

/** Half-circle gauge (0–100) with banded track. */
export function Gauge({ value, color, label, bands = [] }) {
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  const W = 260;
  const H = 150;
  const cx0 = W / 2;
  const cy0 = 136;
  const R = 112;
  const pt = (p) => {
    const a = Math.PI * (1 - p / 100);
    return [cx0 + R * Math.cos(a), cy0 - R * Math.sin(a)];
  };
  const arc = (from, to) => {
    const [x1, y1] = pt(from);
    const [x2, y2] = pt(to);
    return `M${x1.toFixed(1)},${y1.toFixed(1)} A${R},${R} 0 0 1 ${x2.toFixed(1)},${y2.toFixed(1)}`;
  };
  const [nx, ny] = pt(v);
  return (
    <div className="gauge">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${label}: ${value ?? "unavailable"} out of 100`}>
        <path d={arc(0, 100)} style={{ fill: "none", stroke: "var(--panel-3)" }} strokeWidth="14" strokeLinecap="round" />
        {bands.map((b) => (
          <path key={b.from} d={arc(b.from + 0.6, b.to - 0.6)} style={{ fill: "none", stroke: b.color, opacity: 0.22 }} strokeWidth="14" />
        ))}
        {value != null && v > 0.5 && (
          <path d={arc(0, v)} style={{ fill: "none", stroke: color }} strokeWidth="14" strokeLinecap="round" />
        )}
        {value != null && <circle cx={nx} cy={ny} r="7" style={{ fill: "var(--panel)", stroke: color }} strokeWidth="3" />}
      </svg>
      <div className="gauge-value">
        <strong style={{ color }}>{value == null ? "—" : value.toFixed(value % 1 ? 1 : 0)}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

export function Legend({ items, className }) {
  return (
    <div className={cx("legend", className)}>
      {items.map((i) => (
        <span className="legend-item" key={i.label}>
          <i className={i.square ? "sq" : undefined} style={{ background: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}
